// framework/client/core/ReactiveDataStore.js

export class ReactiveDataStore {
    constructor(appClient) {
        this.appClient = appClient;
        this.log = new this.appClient.Logger(this.constructor.name);
        this.data = new Map();
        this.rdsConfig = null;
        this._boundLoginHandler = this.connect.bind(this);
        this._boundTriggerHandlers = new Map();
    }

    async initialize() {
        const clientConfig = this.appClient.sessionTokenHandler
            ? await this.appClient.sessionTokenHandler.getClientConfig()
            : this.appClient.config;
        this.rdsConfig = clientConfig.reactiveData || { dataSets: {} };

        for (const setName in this.rdsConfig.dataSets) {
            this.data.set(setName, new Map());
        }

        this.appClient.events.local.on("USER_LOGGED_IN", this._boundLoginHandler);
        this._listenToServerEvents();
        this._listenForLoadTriggers();
        this._listenForScopeRefreshEvents();

        this.log.info("ReactiveDataStore initialized.");
    }

    _listenForLoadTriggers() {
        for (const [setName, config] of Object.entries(this.rdsConfig.dataSets)) {
            if (config.loadTrigger && config.loadTrigger.eventName) {
                const handler = async (eventData) => {
                    this.log.info(`RDS: Load trigger '${config.loadTrigger.eventName}' fired for dataset '${setName}'.`);

                    const filterContext = {};
                    if (config.loadTrigger.contextKey) {
                        const keyPath = config.loadTrigger.contextKey.split('.');
                        let value = eventData;
                        for (const key of keyPath) {
                            value = value ? value[key] : undefined;
                        }

                        if (value !== undefined && value !== null) {
                            const filterKey = config.loadTrigger.filterKey || keyPath[keyPath.length - 1];
                            filterContext[filterKey] = value;
                        } else {
                            this.log.warn(`RDS: Trigger for '${setName}' fired, but context key '${config.loadTrigger.contextKey}' not found or is null/undefined in event data. Clearing data for this set.`);
                            const collectionCache = this.data.get(setName);
                            if (collectionCache) collectionCache.clear();
                            this._emitRdsEvent('loaded', setName); // CHANGED: Use common emitter
                            return;
                        }
                    }

                    try {
                        const documents = await this.appClient.rpcClient.call('ReactiveDataRpc.getDataSet', {
                            dataSetName: setName,
                            filterContext: filterContext
                        });

                        const collectionCache = this.data.get(setName) || new Map();
                        collectionCache.clear();
                        documents.forEach(doc => collectionCache.set(doc._id, doc));
                        this.data.set(setName, collectionCache);

                        this.log.info(`RDS: Loaded ${collectionCache.size} items for '${setName}' via trigger.`);
                        this._emitRdsEvent('loaded', setName); // CHANGED: Use common emitter
                    } catch (error) {
                        this.log.error(`RDS: Failed to fetch on-demand data for '${setName}':`, error);
                    }
                };

                const boundHandler = handler.bind(this);
                this._boundTriggerHandlers.set(setName, { eventName: config.loadTrigger.eventName, handler: boundHandler });
                this.appClient.events.local.on(config.loadTrigger.eventName, boundHandler);
            }
        }
    }


    _listenForScopeRefreshEvents() {
        this.appClient.events.server.on('dataScopeRefreshed', async ({ scope }) => {
            this.log.info(`RDS: Received 'dataScopeRefreshed' event for scope '${scope}'. Reloading all associated datasets.`);

            // Find all datasets that are tied to this scope.
            // A project scope matches the projectId. The 'shared' scope is also considered.
            const projectId = this.appClient.getSharedData('selectedProjectId');
            const isSharedScope = scope === 'shared';
            const isCurrentProjectScope = scope === projectId;

            for (const [setName, config] of Object.entries(this.rdsConfig.dataSets)) {
                let shouldReload = false;

                // Reload if the dataset is in the 'shared' scope and the event was for 'shared'.
                if (isSharedScope && (!config.loadTrigger || config.loadCondition)) {
                    shouldReload = true;
                }
                // Reload if the dataset is project-specific and the event was for the current project.
                else if (isCurrentProjectScope && config.loadTrigger?.contextKey === 'projectData._id') {
                    shouldReload = true;
                }

                if (shouldReload) {
                    this.log.debug(`Reloading dataset '${setName}' due to scope refresh.`);
                    // Use the existing trigger handler logic to perform the reload.
                    const handlerConfig = this._boundTriggerHandlers.get(setName);
                    if (handlerConfig && handlerConfig.handler) {
                        // We need to construct a mock event payload that the handler expects.
                        const mockEventData = { projectData: { _id: projectId } };
                        await handlerConfig.handler(mockEventData);
                    } else {
                        // Fallback for non-triggered datasets (like 'shared' ones)
                        await this._fetchAndReload(setName);
                    }
                }
            }
        });
    }


    async connect() {
        const setsToLoad = Object.entries(this.rdsConfig.dataSets)
            .filter(([_, config]) => {
                return config.loadCondition === 'always' || (config.loadCondition === 'auth' && this.appClient.isLoggedIn());
            })
            .map(([setName, _]) => setName);

        if (setsToLoad.length > 0) {
            try {
                const initialState = await this.appClient.rpcClient.call('ReactiveDataRpc.getInitialState', { dataSetNames: setsToLoad });
                for (const [setName, documents] of Object.entries(initialState)) {
                    const collectionCache = new Map();
                    documents.forEach(doc => collectionCache.set(doc._id, doc));
                    this.data.set(setName, collectionCache);
                    this.log.info(`RDS: Loaded ${collectionCache.size} initial items for '${setName}'.`);
                    this._emitRdsEvent('loaded', setName); // CHANGED: Use common emitter
                }
            } catch (error) {
                this.log.error("RDS: Failed to get initial state.", error);
            }
        }

        await this._proactivelyLoadTriggeredData();
    }

    async _proactivelyLoadTriggeredData() {
        this.log.debug("RDS: Proactively checking for triggered data to load on init...");
        for (const [setName, config] of Object.entries(this.rdsConfig.dataSets)) {
            if (config.loadTrigger && config.loadTrigger.contextKey) {
                const keyPath = config.loadTrigger.contextKey.split('.');
                const sharedDataKey = keyPath[0];
                const value = this.appClient.getSharedData(sharedDataKey);

                if (value) {
                    this.log.info(`RDS: Found initial data for '${sharedDataKey}'. Proactively loading dataset '${setName}'.`);
                    const handlerConfig = this._boundTriggerHandlers.get(setName);
                    if (handlerConfig && handlerConfig.handler) {
                        await handlerConfig.handler({ [sharedDataKey]: value });
                    }
                }
            }
        }
    }

    _listenToServerEvents() {
        for (const setName in this.rdsConfig.dataSets) {
            this.appClient.events.server.on(`rds:updated:${setName}`, (doc) => this._handleUpsert(setName, doc));
            // CHANGED: Route 'created' events to a dedicated handler
            this.appClient.events.server.on(`rds:created:${setName}`, (doc) => this._handleCreate(setName, doc));
            this.appClient.events.server.on(`rds:deleted:${setName}`, (doc) => this._handleDelete(setName, doc));
            this.appClient.events.server.on(`rds:full-reload:${setName}`, (payload) => {
                this.log.info(`Received full-reload signal for dataset '${setName}'. Refreshing from server...`, payload);
                this._fetchAndReload(setName);
            });
        }
    }

    async _fetchAndReload(dataSetName) {
        try {
            const newState = await this.appClient.rpcClient.call('ReactiveDataRpc.getInitialState', { dataSetNames: [dataSetName] });
            const documents = newState[dataSetName];

            if (documents) {
                const collectionCache = this.data.get(dataSetName) || new Map();
                collectionCache.clear();
                documents.forEach(doc => collectionCache.set(doc._id, doc));
                this.data.set(dataSetName, collectionCache);

                this.log.info(`RDS: Successfully reloaded ${collectionCache.size} items for '${dataSetName}'.`);
                // CHANGED: Use common emitter for the generic 'reloaded' event
                this._emitRdsEvent('reloaded', null, { setName: dataSetName });
            }
        } catch (error) {
            this.log.error(`RDS: Failed to perform full reload for dataset '${dataSetName}'.`, error);
        }
    }

    // NEW: Dedicated handler for 'created' events
    _handleCreate(setName, doc) {
        const collectionCache = this.data.get(setName);
        if (collectionCache) {
            collectionCache.set(doc._id, doc);
            this.log.info(`created:${setName}`, doc);
            this._emitRdsEvent('created', setName, doc); // CHANGED: Use common emitter
        }
    }

    _handleUpsert(setName, doc) {
        const collectionCache = this.data.get(setName);
        if (collectionCache) {
            collectionCache.set(doc._id, doc);
            this.log.info(`updated:${setName}`, doc);
            this._emitRdsEvent('updated', setName, doc); // CHANGED: Use common emitter
        }
    }

    _handleDelete(setName, doc) {
        const collectionCache = this.data.get(setName);
        if (collectionCache) {
            collectionCache.delete(doc._id);
            this.log.info(`deleted:${setName}`, doc);
            this._emitRdsEvent('deleted', setName, doc); // CHANGED: Use common emitter
        }
    }

    // --- PUBLIC API ---
    async get(dataSetName, id) {
        return this.data.get(dataSetName)?.get(id.toString());
    }

    async getAll(dataSetName) {
        const collectionCache = this.data.get(dataSetName);
        return collectionCache ? Array.from(collectionCache.values()) : [];
    }

    async query(dataSetName, filterFn) {
        const allItems = await this.getAll(dataSetName);
        return allItems.filter(filterFn);
    }

    // NEW: Common event emitter method
    _emitRdsEvent(type, setName, data) {
        // For events specific to a dataset (loaded, created, updated, deleted)
        if (setName) {
            const eventName = `rds-client:${type}:${setName}`;
            this.appClient.events.fireLocal(eventName, data);
        } else {
            // For generic events like 'reloaded'
            const eventName = `rds-client:${type}`;
            this.appClient.events.fireLocal(eventName, data);
        }
    }

    destroy() {
        this.appClient.events.local.off("USER_LOGGED_IN", this._boundLoginHandler);
        if (this._boundTriggerHandlers.size > 0) {
            this._boundTriggerHandlers.forEach(({ eventName, handler }, setName) => {
                this.appClient.events.local.off(eventName, handler);
                this.log.debug(`RDS: Unregistered load trigger for '${setName}'.`);
            });
            this._boundTriggerHandlers.clear();
        }
    }
}
